// ListApps.tsx
import { Badge, Button, Input, InputGroup, Table, Dropdown, useToaster, Message, Modal } from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import ReloadIcon from "@rsuite/icons/Reload";
import { PanelService } from "network/panel/network";
import { EPlatform, IApp, AppStatus, PanelUserAccessScope } from "@bprotsyk/aso-core";
import UpsertAppModal from "ui/modals/apps/app-upsert/upsert-app";
import AppsTable from "./apps-table";
import { emptyAppState } from "constants/app-initial";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCopy, faFilter } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState, useCallback, useRef } from "react";
import "./_apps.scss";

interface ISearchInput {
  search: string;
  setSearch: (value: string) => void;
}

const CustomInputGroup = ({ search, setSearch }: ISearchInput) => (
  <div className="lists-search" style={{ display: 'flex', alignItems: 'center' }}>
    <div style={{ position: 'relative', flex: 1 }}>
      <input
        type="text"
        placeholder="Пошук..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="lists-search-input"
      />
      <FontAwesomeIcon icon={faSearch} className="lists-search-icon" />
    </div>
  </div>
);

// Function to convert IntegrationVersion enum to readable text
// getIntegrationVersionText removed - integrationVersion field no longer exists

// Function to check if bundle is valid Android package format
const isValidAndroidPackage = (bundle: string): boolean => {
  if (!bundle) return false;
  
  // Android package format: com.example.app or similar
  // Should contain only letters, numbers, dots, and underscores
  // Should start with a letter and contain at least one dot
  const androidPackageRegex = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;
  return androidPackageRegex.test(bundle);
};

// Function to get Google Play Store URL
const getGooglePlayUrl = (bundle: string): string => {
  return `https://play.google.com/store/apps/details?id=${encodeURIComponent(bundle)}`;
};

// Function to get status color for the dot
const getStatusColor = (status: AppStatus, isChanged: boolean = false): string => {
  if (isChanged) {
    return "#ffc107"; // жовтий для змінених статусів
  }
  
  switch (status) {
    case AppStatus.ARCHIVED:
      return "#6c757d"; // сіра
    case AppStatus.IN_DEVELOPMENT:
      return "#007bff"; // синя
    case AppStatus.READY:
      return "#28a745"; // зелена
    default:
      return "#6c757d"; // сіра за замовчуванням
  }
};

// Function to get status text interpretation
const getStatusText = (status: AppStatus): string => {
  switch (status) {
    case AppStatus.ARCHIVED:
      return "Архів";
    case AppStatus.IN_DEVELOPMENT:
      return "В розробці";
    case AppStatus.READY:
      return "Готовий";
    default:
      return "Невідомо";
  }
};

// Function to get available status transitions
const getAvailableStatusTransitions = (currentStatus: AppStatus): AppStatus[] => {
  switch (currentStatus) {
    case AppStatus.ARCHIVED:
      return [AppStatus.IN_DEVELOPMENT, AppStatus.READY];
    case AppStatus.IN_DEVELOPMENT:
      return [AppStatus.ARCHIVED, AppStatus.READY];
    case AppStatus.READY:
      return [AppStatus.ARCHIVED, AppStatus.IN_DEVELOPMENT];
    default:
      return [AppStatus.ARCHIVED, AppStatus.IN_DEVELOPMENT, AppStatus.READY];
  }
};

// Filter interface and local storage key
interface IFilters {
  hideArchived: boolean;
  hideInDevelopment: boolean;
}

const FILTERS_STORAGE_KEY = 'apps-filters';

// Load filters from localStorage
const loadFilters = (): IFilters => {
  try {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Error loading filters from localStorage:', error);
  }
  return {
    hideArchived: false,
    hideInDevelopment: false,
  };
};

// Save filters to localStorage
const saveFilters = (filters: IFilters) => {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.error('Error saving filters to localStorage:', error);
  }
};


// Header height constants
const HEADER_HEIGHT = 56; // Main header height
const SUB_HEADER_HEIGHT = 64; // Sub-header height
const TOTAL_HEADER_HEIGHT = HEADER_HEIGHT + SUB_HEADER_HEIGHT;

const Apps: React.FC = () => {
  const [apps, setApps] = useState<IApp[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<IApp | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [tableHeight, setTableHeight] = useState(window.innerHeight - TOTAL_HEADER_HEIGHT);
  const [selectedPlatform, setSelectedPlatform] = useState<EPlatform | null>(null);
  // const [selectedVersionModal, setSelectedVersionModal] = useState<IntegrationVersion|null>(null); // IntegrationVersion removed
  const [saving, setSaving] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ x: number; y: number } | null>(null);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  
  // Toast notification
  const toaster = useToaster();
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortType, setSortType] = useState<'asc' | 'desc'>('asc');
  
  // Filters state
  const [filters, setFilters] = useState<IFilters>(loadFilters());

  useEffect(() => {
    const handleResize = () => {
      setTableHeight(window.innerHeight - TOTAL_HEADER_HEIGHT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch apps on component mount and subscribe to updates
  useEffect(() => {
    console.log('Компонент Apps змонтовано, підписуємось на оновлення...');
    
    // Початкове завантаження даних
    fetchApps();
    console.log(apps);
    
    // Підписуємось на оновлення в реальному часі
    const unsubscribe = PanelService.subscribeToAppsUpdates((updatedApps) => {
      console.log('Отримано оновлення списку додатків через WebSocket:', updatedApps.length);
      
    if (updatedApps && updatedApps.length > 0) {
      console.log('Оновлюємо стан компонента новими даними');
      console.log(updatedApps);
      setApps(updatedApps);
    } else {
      console.warn('Отримано порожній список додатків з WebSocket');
    }
    });
    
    // Відписуємось при розмонтуванні компонента
    return () => {
      console.log('Компонент Apps розмонтовано, відписуємось від оновлень');
      unsubscribe();
    };
  }, []);

  // Fetch apps from the server
  const fetchApps = async () => {
    console.log('Запит на отримання списку додатків...');
    setLoading(true);
    try {
      const data = await PanelService.getApps();
      console.log('Отримано список додатків:', data ? (Array.isArray(data) ? data.length : 'object') : 'немає даних');
      
      if (Array.isArray(data)) {
        setApps(data);
      } else if (data && typeof data === 'object') {
        setApps(Object.values(data));
      } else {
        console.error('Неочікуваний формат даних:', data);
        setApps([]);
      }
    } catch (error) {
      console.error("Помилка при отриманні списку додатків:", error);
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleAppClick = (app: IApp) => {
    setSelectedApp(app);
  };

  const handleModalClose = () => {
    setSelectedApp(null);
    fetchApps();
  };

  const handleSelectedPlatform = (app: IApp, platform: string) => {
    setSelectedApp(app);
    setSelectedPlatform(platform as EPlatform);
  };

  // handleSelectVersionModal removed - integrationVersion field no longer exists
  const handleSelectVersionModal = (app: IApp) => {
    // No longer checking integrationVersion
  };

  // Handle status cell click
  const handleStatusCellClick = (appId: number, event: React.MouseEvent) => {
    if (openDropdownId === appId) {
      setOpenDropdownId(null);
      setDropdownPosition(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        x: rect.left,
        y: rect.top + window.scrollY
      });
      setOpenDropdownId(appId);
    }
  };

  // State for status change confirmation dialog
  const [statusChangeDialog, setStatusChangeDialog] = useState<{
    appId: number;
    appName: string;
    currentStatus: AppStatus;
    newStatus: AppStatus;
  } | null>(null);

  // Handle status change with confirmation
  const handleStatusChange = (appId: number, newStatus: AppStatus) => {
    const app = apps.find(a => a.id === appId);
    if (!app) return;

    setStatusChangeDialog({
      appId,
      appName: app.name,
      currentStatus: app.status,
      newStatus
    });
    
    setOpenDropdownId(null);
    setDropdownPosition(null);
  };

  // Confirm and save status change
  const confirmStatusChange = async () => {
    if (!statusChangeDialog) return;

    setSaving(true);
    try {
      await PanelService.updateAppStatuses([
        { appId: statusChangeDialog.appId, status: statusChangeDialog.newStatus }
      ]);
      
      toaster.push(
        <Message showIcon type="success" closable={false}>
          Статус успішно оновлено
        </Message>,
        { placement: 'topCenter', duration: 2000 }
      );
      
      await fetchApps();
    } catch (error) {
      console.error('Error saving status:', error);
      
      toaster.push(
        <Message showIcon type="error" closable={false}>
          Помилка при оновленні статусу
        </Message>,
        { placement: 'topCenter', duration: 3000 }
      );
    } finally {
      setSaving(false);
      setStatusChangeDialog(null);
    }
  };


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId !== null) {
        const dropdownElement = dropdownRefs.current.get(openDropdownId);
        const clickedElement = event.target as HTMLElement;
        
        if (clickedElement.closest('.status-cell')) {
          return;
        }
        
        if (dropdownElement && !dropdownElement.contains(clickedElement)) {
          setOpenDropdownId(null);
          setDropdownPosition(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  // Filter and sort apps
  const filteredApps = apps
    .filter((app) => {
      // Filter out invalid apps (no ID or no name)
      if (!app.id || app.id === 0 || !app.name) return false;
      
      const searchLower = search.toLowerCase();
      const appIdString = `#${app.id}`;
      const appName = app.name?.toLowerCase() || '';
      const bundle = app.bundle?.toLowerCase() || '';
      const domain = app.domainParams?.name?.toLowerCase() || '';
      
      const matchesSearch = appIdString.includes(searchLower) ||
        appName.includes(searchLower) ||
        bundle.includes(searchLower) ||
        domain.includes(searchLower);
      
      if (!matchesSearch) return false;
      
      if (filters.hideArchived && app.status === AppStatus.ARCHIVED) return false;
      if (filters.hideInDevelopment && app.status === AppStatus.IN_DEVELOPMENT) return false;
      
      return true;
    })
    .sort((a, b) => {
      if (!sortColumn) return 0;
      
      let aValue: any = '';
      let bValue: any = '';
      
      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'bundle':
          aValue = (a.bundle || '').toLowerCase();
          bValue = (b.bundle || '').toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        // integrationVersion case removed - field no longer exists
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortType === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortType === 'asc' ? 1 : -1;
      return 0;
    });

  // Handle sorting
  const handleSort = (dataKey: string, sortType: 'asc' | 'desc' | undefined) => {
    setSortColumn(dataKey);
    setSortType(sortType || 'asc');
  };

  // Handle filter changes
  const handleFilterChange = (filterKey: string) => {
    const newFilters = {
      ...filters,
      [filterKey as keyof IFilters]: !filters[filterKey as keyof IFilters],
    };
    setFilters(newFilters);
    saveFilters(newFilters);
  };


  return (
    <div className="apps">
      <div className="apps-content">
        {!selectedApp && !createModalOpen ? (
          <AppsTable
            apps={filteredApps}
            loading={loading}
            onAppClick={handleAppClick}
            onPlatformClick={handleSelectedPlatform}
            onStatusChange={handleStatusChange}
            openDropdownId={openDropdownId}
            dropdownPosition={dropdownPosition}
            onStatusCellClick={handleStatusCellClick}
            dropdownRefs={dropdownRefs}
            sortColumn={sortColumn}
            sortType={sortType}
            onSort={handleSort}
            tableHeight={tableHeight}
            search={search}
            onSearchChange={handleSearchChange}
            onCreateApp={() => setCreateModalOpen(true)}
            onRefresh={fetchApps}
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        ) : (
          <UpsertAppModal
            onClose={() => {
              setSelectedApp(null);
              setCreateModalOpen(false);
              handleModalClose();
            }}
            app={selectedApp || undefined}
            create={createModalOpen}
            selectedPlatform={selectedPlatform}
            // selectedIntegration={selectedVersionModal} // IntegrationVersion removed
          />
        )}

        {/* Status Change Confirmation Dialog */}
        <Modal
          open={statusChangeDialog !== null}
          onClose={() => setStatusChangeDialog(null)}
          size="xs"
        >
          <Modal.Header>
            <Modal.Title>Підтвердження зміни статусу</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {statusChangeDialog && (
              <div style={{ padding: '12px 0' }}>
                <div style={{ marginBottom: '12px', color: '#ecf0f1' }}>
                  <strong>ID:</strong> #{statusChangeDialog.appId}
                </div>
                <div style={{ marginBottom: '16px', color: '#ecf0f1' }}>
                  <strong>Назва:</strong> {statusChangeDialog.appName}
                </div>
                <div style={{ color: '#ecf0f1', lineHeight: '1.6' }}>
                  Змінити статус з <strong>"{getStatusText(statusChangeDialog.currentStatus)}"</strong> на <strong>"{getStatusText(statusChangeDialog.newStatus)}"</strong>?
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setStatusChangeDialog(null)} appearance="subtle">
              Скасувати
            </Button>
            <Button onClick={confirmStatusChange} appearance="primary" loading={saving}>
              Підтвердити
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default Apps;
